import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BookOpenText, Plus, X, Star, Trash2, Sparkles, Search, Target, Flame, TrendingUp, BookMarked,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePet } from '../contexts/PetContext';
import { PetAvatar } from './PetEvolution';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHELVES = [
  { key: 'reading', label: 'Läser nu', emoji: '📖', color: '#C8B6FF' },
  { key: 'wishlist', label: 'Vill läsa', emoji: '💭', color: '#A2D2FF' },
  { key: 'finished', label: 'Klarat av', emoji: '✅', color: '#B9FBC0' },
];

function ProgressRing({ pct, size = 56, stroke = 6, color = '#C8B6FF' }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = Math.max(0, Math.min(100, pct)) / 100 * c;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#00000020" strokeWidth={stroke} fill="none" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none"
        strokeDasharray={`${dash} ${c}`} strokeLinecap="round" />
    </svg>
  );
}

function BookCard({ book, onLog, onEdit, onDelete }) {
  const pct = book.total_pages ? Math.round((book.current_page / book.total_pages) * 100) : 0;
  return (
    <div
      className="neo-card p-3 relative overflow-hidden"
      style={{ backgroundColor: book.cover_color || '#FFD6E8' }}
      data-testid={`reading-book-${book.id}`}
    >
      <div className="flex gap-3">
        {book.thumbnail_url ? (
          <img src={book.thumbnail_url} alt="" className="w-14 h-20 object-cover rounded-md border-2 border-black flex-shrink-0" />
        ) : (
          <div className="w-14 h-20 rounded-md border-2 border-black flex items-center justify-center bg-white/60 text-2xl flex-shrink-0">
            📚
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h4 className="font-bold font-['Fredoka'] text-sm leading-tight truncate">{book.title}</h4>
          {book.author && <p className="text-[11px] opacity-70 truncate">{book.author}</p>}
          {book.genre && <p className="text-[10px] opacity-60 italic">{book.genre}</p>}
          {book.shelf === 'finished' && book.rating > 0 && (
            <div className="flex gap-0.5 mt-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} className="w-3 h-3" fill={n <= book.rating ? '#FFD700' : 'none'} stroke="#000" />
              ))}
            </div>
          )}
          {book.total_pages > 0 && book.shelf !== 'wishlist' && (
            <div className="mt-1">
              <div className="flex items-center justify-between text-[10px]">
                <span className="font-mono opacity-70">{book.current_page} / {book.total_pages}</span>
                <span className="font-bold">{pct}%</span>
              </div>
              <div className="h-1.5 bg-white/60 rounded-full overflow-hidden border border-black">
                <div className="h-full bg-black transition-all" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-1.5 mt-2 flex-wrap">
        {book.shelf !== 'finished' && (
          <button
            onClick={() => onLog(book)}
            className="neo-btn px-2 py-1 text-[10px] bg-white flex items-center gap-1"
            data-testid={`reading-log-${book.id}`}
          >
            <Plus className="w-3 h-3" /> Logga
          </button>
        )}
        <button
          onClick={() => onEdit(book)}
          className="neo-btn px-2 py-1 text-[10px] bg-white"
          data-testid={`reading-edit-${book.id}`}
        >
          Redigera
        </button>
        <button
          onClick={() => onDelete(book)}
          className="neo-btn p-1 text-[10px] bg-white ml-auto"
          data-testid={`reading-delete-${book.id}`}
          title="Ta bort"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

function AddBookModal({ onClose, onCreated }) {
  const [tab, setTab] = useState('search'); // search | manual
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [form, setForm] = useState({
    title: '', author: '', total_pages: '', shelf: 'reading', genre: '', isbn: '', thumbnail_url: '',
  });
  const [saving, setSaving] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/reading/search?q=${encodeURIComponent(q.trim())}`, { withCredentials: true });
      setResults(data.items || []);
      if ((data.items || []).length === 0) toast.info('Inga träffar — testa en annan sökning');
    } catch {
      toast.error('Sökning misslyckades');
    } finally { setSearching(false); }
  };

  const pickResult = (r) => {
    setForm({ ...form, title: r.title, author: r.author, total_pages: String(r.total_pages || ''), genre: r.genre, isbn: r.isbn, thumbnail_url: r.thumbnail_url });
    setTab('manual');
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Titel krävs'); return; }
    setSaving(true);
    try {
      const payload = { ...form, total_pages: parseInt(form.total_pages || '0', 10) || 0 };
      await axios.post(`${API}/reading/books`, payload, { withCredentials: true });
      toast.success('Bok tillagd 📚');
      onCreated();
      onClose();
    } catch { toast.error('Kunde inte lägga till'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="neo-card bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
        data-testid="reading-add-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2">
            <BookOpenText className="w-5 h-5" /> Lägg till bok
          </h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white" data-testid="reading-add-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setTab('search')}
            className={`neo-btn px-3 py-1.5 text-xs font-bold ${tab === 'search' ? 'bg-[#C8B6FF]' : 'bg-white'}`}
            data-testid="reading-tab-search"
          ><Search className="w-3 h-3 inline mr-1" /> Sök (Google Books)</button>
          <button
            onClick={() => setTab('manual')}
            className={`neo-btn px-3 py-1.5 text-xs font-bold ${tab === 'manual' ? 'bg-[#C8B6FF]' : 'bg-white'}`}
            data-testid="reading-tab-manual"
          >Manuell</button>
        </div>

        {tab === 'search' && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && search()}
                placeholder="Titel eller författare…"
                className="neo-input flex-1 text-sm"
                data-testid="reading-search-input"
              />
              <button onClick={search} disabled={searching} className="neo-btn px-3 py-2 bg-[#C8B6FF] text-sm font-bold" data-testid="reading-search-btn">
                {searching ? '...' : 'Sök'}
              </button>
            </div>
            <div className="space-y-1 max-h-[320px] overflow-y-auto">
              {results.map((r, i) => (
                <button
                  key={i}
                  onClick={() => pickResult(r)}
                  className="w-full text-left flex gap-2 p-2 neo-card bg-[#FFF8E7] hover:bg-[#FFEAA7] transition-colors"
                  data-testid={`reading-search-result-${i}`}
                >
                  {r.thumbnail_url ? <img src={r.thumbnail_url} alt="" className="w-10 h-14 object-cover rounded border border-black" /> : <div className="w-10 h-14 bg-white rounded border border-black flex items-center justify-center text-lg">📘</div>}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-xs truncate">{r.title}</p>
                    <p className="text-[10px] opacity-70 truncate">{r.author || 'okänd'}</p>
                    <p className="text-[10px] opacity-60">{r.total_pages ? `${r.total_pages} sidor` : ''} {r.genre ? `· ${r.genre}` : ''}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {tab === 'manual' && (
          <div className="space-y-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel *" className="neo-input w-full text-sm" data-testid="reading-form-title" />
            <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Författare" className="neo-input w-full text-sm" data-testid="reading-form-author" />
            <div className="grid grid-cols-2 gap-2">
              <input value={form.total_pages} onChange={(e) => setForm({ ...form, total_pages: e.target.value })} placeholder="Antal sidor" type="number" className="neo-input w-full text-sm" data-testid="reading-form-pages" />
              <input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} placeholder="Genre" className="neo-input w-full text-sm" data-testid="reading-form-genre" />
            </div>
            <div className="flex gap-1 flex-wrap">
              {SHELVES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setForm({ ...form, shelf: s.key })}
                  className={`neo-btn px-2 py-1 text-[11px] font-bold ${form.shelf === s.key ? '' : 'bg-white'}`}
                  style={{ backgroundColor: form.shelf === s.key ? s.color : undefined }}
                  data-testid={`reading-form-shelf-${s.key}`}
                >{s.emoji} {s.label}</button>
              ))}
            </div>
            <button onClick={save} disabled={saving} className="neo-btn w-full py-2 bg-[#B9FBC0] font-bold text-sm" data-testid="reading-form-save">
              {saving ? 'Sparar…' : 'Spara bok'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LogPagesModal({ book, onClose, onLogged }) {
  const [pages, setPages] = useState('');
  const [minutes, setMinutes] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const p = parseInt(pages || '0', 10) || 0;
    const m = parseInt(minutes || '0', 10) || 0;
    if (p === 0 && m === 0) { toast.error('Logga minst sidor eller minuter'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/reading/books/${book.id}/log`, { pages_read: p, minutes_read: m }, { withCredentials: true });
      toast.success(`+${data.points_awarded} poäng${data.book_finished ? ' · Bok klar! 🎉' : ''}`);
      onLogged();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Misslyckades'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="reading-log-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Fredoka'] font-bold text-lg">Logga läsning</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs opacity-70 mb-2">{book.title}</p>
        <div className="space-y-2">
          <label className="block">
            <span className="text-xs font-bold">Sidor läst</span>
            <input value={pages} onChange={(e) => setPages(e.target.value)} type="number" placeholder="t.ex. 20" className="neo-input w-full text-sm" data-testid="reading-log-pages-input" autoFocus />
          </label>
          <label className="block">
            <span className="text-xs font-bold">Minuter läst</span>
            <input value={minutes} onChange={(e) => setMinutes(e.target.value)} type="number" placeholder="valfri" className="neo-input w-full text-sm" data-testid="reading-log-minutes-input" />
          </label>
          <button onClick={submit} disabled={saving} className="neo-btn w-full py-2 bg-[#C8B6FF] font-bold text-sm" data-testid="reading-log-submit">
            {saving ? 'Sparar…' : 'Logga'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditBookModal({ book, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: book.title || '', author: book.author || '', total_pages: String(book.total_pages || ''),
    current_page: String(book.current_page || ''), shelf: book.shelf, genre: book.genre || '',
    rating: book.rating || 0, notes: book.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/reading/books/${book.id}`, {
        ...form,
        total_pages: parseInt(form.total_pages || '0', 10) || 0,
        current_page: parseInt(form.current_page || '0', 10) || 0,
        rating: parseInt(form.rating || 0, 10) || 0,
      }, { withCredentials: true });
      toast.success('Sparat');
      onSaved();
      onClose();
    } catch { toast.error('Kunde inte spara'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white w-full max-w-md max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="reading-edit-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Fredoka'] font-bold text-lg">Redigera bok</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel" className="neo-input w-full text-sm" />
          <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} placeholder="Författare" className="neo-input w-full text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.current_page} onChange={(e) => setForm({ ...form, current_page: e.target.value })} type="number" placeholder="Sida" className="neo-input w-full text-sm" />
            <input value={form.total_pages} onChange={(e) => setForm({ ...form, total_pages: e.target.value })} type="number" placeholder="Av" className="neo-input w-full text-sm" />
          </div>
          <input value={form.genre} onChange={(e) => setForm({ ...form, genre: e.target.value })} placeholder="Genre" className="neo-input w-full text-sm" />
          <div className="flex gap-1 flex-wrap">
            {SHELVES.map((s) => (
              <button key={s.key} onClick={() => setForm({ ...form, shelf: s.key })}
                className={`neo-btn px-2 py-1 text-[11px] font-bold ${form.shelf === s.key ? '' : 'bg-white'}`}
                style={{ backgroundColor: form.shelf === s.key ? s.color : undefined }}>
                {s.emoji} {s.label}
              </button>
            ))}
          </div>
          <div>
            <p className="text-xs font-bold mb-1">Betyg</p>
            <div className="flex gap-1">
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setForm({ ...form, rating: n })}
                  className={`neo-btn px-2 py-1 text-xs ${form.rating === n ? 'bg-[#FFD700]' : 'bg-white'}`}>
                  {n === 0 ? '—' : '★'.repeat(n)}
                </button>
              ))}
            </div>
          </div>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anteckningar…" className="neo-input w-full text-sm min-h-[60px]" />
          <button onClick={save} disabled={saving} className="neo-btn w-full py-2 bg-[#B9FBC0] font-bold text-sm" data-testid="reading-edit-save">
            {saving ? 'Sparar…' : 'Spara'}
          </button>
        </div>
      </div>
    </div>
  );
}

function GoalModal({ initial, onClose, onSaved }) {
  const [pages, setPages] = useState(String(initial.pages_per_day || ''));
  const [minutes, setMinutes] = useState(String(initial.minutes_per_day || ''));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/reading/goal`, {
        pages_per_day: parseInt(pages || '0', 10) || 0,
        minutes_per_day: parseInt(minutes || '0', 10) || 0,
      }, { withCredentials: true });
      toast.success('Mål uppdaterat 🎯');
      onSaved();
      onClose();
    } catch { toast.error('Kunde inte spara'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="reading-goal-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2"><Target className="w-5 h-5" /> Dagligt mål</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-2">
          <label className="block">
            <span className="text-xs font-bold">Sidor/dag</span>
            <input value={pages} onChange={(e) => setPages(e.target.value)} type="number" placeholder="t.ex. 20" className="neo-input w-full text-sm" data-testid="reading-goal-pages" />
          </label>
          <label className="block">
            <span className="text-xs font-bold">Minuter/dag</span>
            <input value={minutes} onChange={(e) => setMinutes(e.target.value)} type="number" placeholder="t.ex. 15" className="neo-input w-full text-sm" data-testid="reading-goal-minutes" />
          </label>
          <button onClick={save} disabled={saving} className="neo-btn w-full py-2 bg-[#C8B6FF] font-bold text-sm" data-testid="reading-goal-save">
            {saving ? 'Sparar…' : 'Spara mål'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReadingLog({ onPointsChange }) {
  const { pet } = usePet();
  const [books, setBooks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [logging, setLogging] = useState(null);
  const [goalOpen, setGoalOpen] = useState(false);
  const [activeShelf, setActiveShelf] = useState('reading');
  const [recs, setRecs] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        axios.get(`${API}/reading/books`, { withCredentials: true }),
        axios.get(`${API}/reading/stats`, { withCredentials: true }),
      ]);
      setBooks(b.data.items || []);
      setStats(s.data || null);
    } catch {
      toast.error('Kunde inte hämta läslogg');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const delBook = async (b) => {
    if (!window.confirm(`Ta bort "${b.title}"?`)) return;
    try {
      await axios.delete(`${API}/reading/books/${b.id}`, { withCredentials: true });
      await fetchAll();
      toast.success('Borttagen');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const fetchRecs = async () => {
    setRecsLoading(true);
    try {
      const { data } = await axios.post(`${API}/reading/recommend`, {}, { withCredentials: true });
      setRecs(data.recommendations || []);
      if ((data.recommendations || []).length === 0) toast.info(data.message || 'Inga rekommendationer ännu');
    } catch { toast.error('AI misslyckades'); }
    finally { setRecsLoading(false); }
  };

  const shelfBooks = books.filter((b) => b.shelf === activeShelf);
  const goal = stats?.goal || { pages_per_day: 0, minutes_per_day: 0 };
  const goalPct = goal.pages_per_day > 0
    ? Math.round(((stats?.pages_today || 0) / goal.pages_per_day) * 100)
    : goal.minutes_per_day > 0
      ? Math.round(((stats?.minutes_today || 0) / goal.minutes_per_day) * 100)
      : 0;

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar läslogg…</div>;

  return (
    <div className="space-y-4" data-testid="reading-log-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
          <BookOpenText className="w-6 h-6" /> Läslogg
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setGoalOpen(true)} className="neo-btn px-3 py-1.5 bg-white text-sm font-bold flex items-center gap-1" data-testid="reading-open-goal">
            <Target className="w-4 h-4" /> Mål
          </button>
          <button onClick={() => setAddOpen(true)} className="neo-btn px-3 py-1.5 bg-[#C8B6FF] text-sm font-bold flex items-center gap-1" data-testid="reading-add-open">
            <Plus className="w-4 h-4" /> Ny bok
          </button>
        </div>
      </div>

      {/* Stats + reading pet animation */}
      <div className="neo-card p-4 bg-gradient-to-br from-[#FFF8E7] via-[#FFE8F0] to-[#E8F0FF] overflow-hidden relative">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-center">
          <div className="text-center">
            <p className="text-[10px] opacity-60 uppercase tracking-wider">I år</p>
            <p className="font-['Fredoka'] font-bold text-2xl">{stats?.books_this_year || 0}</p>
            <p className="text-[10px] opacity-70">böcker</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] opacity-60 uppercase tracking-wider">Veckan</p>
            <p className="font-['Fredoka'] font-bold text-2xl">{stats?.pages_week || 0}</p>
            <p className="text-[10px] opacity-70">sidor</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] opacity-60 uppercase tracking-wider flex items-center justify-center gap-1"><Flame className="w-3 h-3" /> Streak</p>
            <p className="font-['Fredoka'] font-bold text-2xl">{stats?.streak || 0}</p>
            <p className="text-[10px] opacity-70">dag(ar)</p>
          </div>
          <div className="text-center">
            <p className="text-[10px] opacity-60 uppercase tracking-wider">Favoritgenre</p>
            <p className="font-['Fredoka'] font-bold text-sm truncate">{stats?.favorite_genre || '—'}</p>
          </div>
          <div className="text-center flex flex-col items-center">
            <p className="text-[10px] opacity-60 uppercase tracking-wider">Dagens mål</p>
            {(goal.pages_per_day > 0 || goal.minutes_per_day > 0) ? (
              <div className="relative">
                <ProgressRing pct={goalPct} size={52} stroke={6} color="#C8B6FF" />
                <div className="absolute inset-0 flex items-center justify-center text-[11px] font-bold">{goalPct}%</div>
              </div>
            ) : (
              <button onClick={() => setGoalOpen(true)} className="text-[11px] underline opacity-70">sätt mål</button>
            )}
          </div>
        </div>

        {/* Pet reading animation */}
        <div className="mt-3 flex items-end gap-2 justify-center" data-testid="reading-pet-animation">
          <div className="relative reading-pet-bob">
            <PetAvatar pet={pet} size={90} />
            <div className="reading-book-float">
              <div className="reading-book-cover">
                <div className="reading-book-line" />
                <div className="reading-book-line short" />
                <div className="reading-book-line" />
              </div>
            </div>
          </div>
          <p className="text-[11px] opacity-60 italic mb-2">
            {stats?.streak >= 3 ? 'Läsande kompis på fire 🔥' : 'Läser tyst bredvid dig…'}
          </p>
        </div>
      </div>

      {/* Shelf selector */}
      <div className="flex gap-2 flex-wrap">
        {SHELVES.map((s) => {
          const count = books.filter((b) => b.shelf === s.key).length;
          return (
            <button
              key={s.key}
              onClick={() => setActiveShelf(s.key)}
              className={`neo-btn px-3 py-1.5 text-sm font-bold flex items-center gap-1 ${activeShelf === s.key ? '' : 'bg-white'}`}
              style={{ backgroundColor: activeShelf === s.key ? s.color : undefined }}
              data-testid={`reading-shelf-${s.key}`}
            >
              <span>{s.emoji}</span>
              <span>{s.label}</span>
              <span className="ml-1 text-[10px] opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      {/* Book grid */}
      {shelfBooks.length === 0 ? (
        <div className="neo-card p-8 text-center bg-white">
          <BookMarked className="w-12 h-12 mx-auto mb-2 opacity-40" />
          <p className="font-bold">Inga böcker på denna hylla än</p>
          <p className="text-xs opacity-60 mt-1">Klicka "Ny bok" för att lägga till</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="reading-shelf-grid">
          {shelfBooks.map((b) => (
            <BookCard key={b.id} book={b} onLog={setLogging} onEdit={setEditing} onDelete={delBook} />
          ))}
        </div>
      )}

      {/* AI recommendations */}
      <div className="neo-card p-4 bg-[#FDF0D5]">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-['Fredoka'] font-bold text-sm flex items-center gap-1"><Sparkles className="w-4 h-4" /> Boktips från Pet Coach</h3>
          <button onClick={fetchRecs} disabled={recsLoading} className="neo-btn px-3 py-1 bg-white text-xs font-bold" data-testid="reading-rec-btn">
            {recsLoading ? 'Tänker…' : 'Få förslag'}
          </button>
        </div>
        {recs.length === 0 ? (
          <p className="text-xs opacity-60 italic">Lägg till några böcker och klicka "Få förslag" för att få AI-rekommendationer baserat på vad du gillat.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="reading-recs">
            {recs.map((r, i) => (
              <div key={i} className="neo-card bg-white p-2">
                <p className="font-bold text-xs">{r.title}</p>
                <p className="text-[11px] opacity-70">{r.author}</p>
                <p className="text-[10px] opacity-80 mt-1 italic">{r.why}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {addOpen && <AddBookModal onClose={() => setAddOpen(false)} onCreated={() => { fetchAll(); onPointsChange?.(); }} />}
      {editing && <EditBookModal book={editing} onClose={() => setEditing(null)} onSaved={() => { fetchAll(); onPointsChange?.(); }} />}
      {logging && <LogPagesModal book={logging} onClose={() => setLogging(null)} onLogged={() => { fetchAll(); onPointsChange?.(); }} />}
      {goalOpen && <GoalModal initial={goal} onClose={() => setGoalOpen(false)} onSaved={fetchAll} />}
    </div>
  );
}
